import { getLocale } from "./i18n";
import rawExtracts from "./data/extracts.json";
import rawSpawns from "./data/spawns.json";
import rawMaps from "./data/maps.json";

/** A floor, deck or basement that can be shown on top of the base artwork. */
export interface MapLayer {
  name: string;
  /** Id of the `<g>` element inside the map's SVG, when the map has one. */
  svgLayer?: string;
  /** Tile template for this floor, when the map is tile based. */
  tilePath?: string;
  /** Whether the upstream data suggests showing it by default. */
  show: boolean;
}

export interface TarkovMap {
  id: string;
  name: string;
  nameJa?: string;
  /** [[x, z], [x, z]] -- Leaflet reads these as latLng = (z, x). */
  bounds: [[number, number], [number, number]];
  svgBounds?: [[number, number], [number, number]];
  /** [scaleX, offsetX, scaleZ, offsetZ] for the Leaflet transformation. */
  transform: [number, number, number, number];
  coordinateRotation: number;
  tileSize: number;
  minZoom: number;
  maxZoom: number;
  tilePath?: string;
  svgPath?: string;
  /** Id of the base `<g>` element inside the SVG. */
  svgLayer?: string;
  layers: MapLayer[];
  author?: string;
  authorLink?: string;
}

export const MAPS = rawMaps as TarkovMap[];

/**
 * Where you can leave a raid, from tarkov.dev.
 *
 * Off by default: the point of the map is what a squad puts on it, and thirty
 * fixed labels on top of that is noise for the people who already know them.
 * The ones who need it are the ones being taught, and they can turn it on.
 */
/**
 * Who may leave through it.
 *
 * `shared` is a door either side can take alone. `coop` is one that needs a PMC
 * and a Scav standing in it together -- the distinction the game makes with
 * `ScavCooperation`, and the one that decides whether a beginner being shown
 * the map can use the exit at all.
 *
 * `transit` is the odd one out: nobody is kept out of a transit, and it does
 * not end the raid at all -- it moves you to another map and the raid carries
 * on. It sits in the same list because it answers the same question ("how do I
 * get out of here"), and because a switch of its own would be one more switch
 * for a thing most people have never used.
 */
export type ExtractFaction = "pmc" | "scav" | "shared" | "coop" | "transit";

export const EXTRACT_FACTIONS: ExtractFaction[] = ["pmc", "scav", "shared", "coop", "transit"];

export interface Extract {
  /** Absent when the game's locale table has no name for it; see fetch-extracts.mjs. */
  name?: string;
  faction: ExtractFaction;
  lat: number;
  lng: number;
}

const EXTRACTS = rawExtracts as Record<string, Extract[]>;

/**
 * Roughly where people come into the map.
 *
 * One per entry rather than one per spawn point: the game lists 75-213 of
 * those per map, and a field of dots is not something a beginner can read.
 * See scripts/fetch-spawns.mjs for how the groups are found and measured.
 */
export interface SpawnSpot {
  /**
   * Index into the map's `entries`: which way in this spot belongs to.
   *
   * null for a spot the game has no entry name for -- spawns added after the
   * copy of the map files the names come from. Those are drawn as a circle and
   * carry no label; see scripts/fetch-spawns.mjs.
   */
  entry: number | null;
  lat: number;
  lng: number;
  /** How far the spot's own points spread, floored so it stays visible. */
  radius: number;
  /** How many of the game's spawn slots are here. */
  count: number;
  /** The biggest spot of its entry: the one that carries the name. */
  label?: boolean;
}

export interface MapSpawns {
  /** The ways in, in the game's own words. null where it only has a code. */
  entries: (string | null)[];
  spots: SpawnSpot[];
}

const SPAWNS = rawSpawns as Record<string, MapSpawns>;

export function spawnsFor(mapId: string): MapSpawns {
  return SPAWNS[mapId] ?? { entries: [], spots: [] };
}

export function hasSpawns(mapId: string): boolean {
  return spawnsFor(mapId).spots.length > 0;
}

export function extractsFor(mapId: string): Extract[] {
  return EXTRACTS[mapId] ?? [];
}

export function hasExtracts(mapId: string): boolean {
  return extractsFor(mapId).length > 0;
}

/**
 * How many of each kind this map holds.
 *
 * The sidebar only offers a filter for kinds the map actually has: Factory has
 * no co-op exit and The Lab has no Scav one, and a row of zeroes is a worse
 * answer than a shorter row.
 */
export function extractCounts(mapId: string): Record<ExtractFaction, number> {
  const counts: Record<ExtractFaction, number> = {
    pmc: 0,
    scav: 0,
    shared: 0,
    coop: 0,
    transit: 0,
  };
  for (const extract of extractsFor(mapId)) counts[extract.faction] += 1;
  return counts;
}

/**
 * Whether we hold extract data for anything at all.
 *
 * The coordinates come from tarkov.dev and nowhere else. When that is
 * unavailable the file is empty, and a switch that is greyed out on every map
 * reads as a broken feature rather than a missing dataset -- so the control is
 * left out entirely until there is something for it to show. That is not
 * hypothetical: it shipped empty on 2026-08-25, and stayed empty until
 * `npm run extracts` was moved off their GraphQL API the next morning.
 */
export function hasAnyExtracts(): boolean {
  return Object.values(EXTRACTS).some((list) => list.length > 0);
}

/**
 * Whether a given rendering can actually draw this floor.
 *
 * Upstream does not carry both renderings for every floor: Shoreline's exist
 * only as groups inside the SVG, Reserve's mostly only as tiles. The floor
 * buttons used to be listed regardless, so on Shoreline's satellite view all
 * three did nothing at all -- which is what someone reported on launch day.
 * The caller uses this to switch the rendering along with the floor rather
 * than offer a button that cannot work.
 */
export function drawsLayer(map: TarkovMap, layer: MapLayer, style: "svg" | "tile"): boolean {
  return style === "svg"
    ? Boolean(map.svgPath) && layer.svgLayer !== undefined
    : Boolean(map.tilePath) && layer.tilePath !== undefined;
}

export const MAPS_BY_ID = new Map(MAPS.map((map) => [map.id, map]));

export function getMap(id: string): TarkovMap {
  return MAPS_BY_ID.get(id) ?? (MAPS[0] as TarkovMap);
}

/**
 * Licences we have actually checked, keyed by the upstream attribution link.
 *
 * The tarkov.dev API gives us an author and a link but no licence, so this is a
 * hand-verified lookup rather than something we can derive. Maps we have not
 * confirmed simply keep the plain author credit -- guessing a licence would be
 * worse than showing none.
 */
const LICENSES: { match: string; name: string; url: string }[] = [
  {
    match: "github.com/the-hideout/tarkov-dev-svg-maps",
    name: "CC BY-NC-SA 4.0",
    url: "https://creativecommons.org/licenses/by-nc-sa/4.0/",
  },
];

export function mapLicense(map: TarkovMap): { name: string; url: string } | null {
  if (!map.authorLink) return null;
  return LICENSES.find((entry) => map.authorLink!.includes(entry.match)) ?? null;
}

/**
 * The Japanese reading is only useful to someone reading Japanese; everyone
 * else gets the name Battlestate uses, which is what the game shows them.
 */
export function mapTitle(map: TarkovMap): string {
  return map.nameJa && getLocale() === "ja" ? `${map.nameJa} / ${map.name}` : map.name;
}
